'use client';

import * as React from 'react';
function arraysEqual(left, right) {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((value, index) => value === right[index]);
}
function startsWithSequence(values, prefix) {
  if (prefix.length > values.length) {
    return false;
  }
  return prefix.every((value, index) => values[index] === value);
}
function endsWithSequence(values, suffix) {
  if (suffix.length > values.length) {
    return false;
  }
  const offset = values.length - suffix.length;
  return suffix.every((value, index) => values[offset + index] === value);
}
function classifyItemChange(previous, next) {
  if (arraysEqual(previous, next)) {
    return 'none';
  }
  if (next.length > previous.length && startsWithSequence(next, previous)) {
    return 'append';
  }
  if (next.length > previous.length && endsWithSequence(next, previous)) {
    return 'prepend';
  }
  return 'other';
}
function isScrollableToBottom(root, threshold) {
  if (!root) {
    return true;
  }
  return root.scrollHeight - root.clientHeight - root.scrollTop <= threshold;
}
export function useMessageListBehavior(parameters) {
  const {
    itemIds,
    estimatedItemSize,
    onReachTop,
    onReachBottom,
    messages,
    hasMoreHistory,
    loadMoreHistory,
    autoScrollEnabled,
    autoScrollBuffer,
    isStreaming
  } = parameters;
  const rootRef = React.useRef(null);
  const rowElementsRef = React.useRef(new Map());
  const previousItemIdsRef = React.useRef(itemIds);
  const itemIdsRef = React.useRef(itemIds);
  itemIdsRef.current = itemIds;
  const anchorRef = React.useRef(null);
  const topReachedRef = React.useRef(false);
  const topLoadInFlightRef = React.useRef(false);
  const resizeFrameRef = React.useRef(0);
  const isMountedRef = React.useRef(true);
  const isStreamingRef = React.useRef(isStreaming);
  isStreamingRef.current = isStreaming;
  const [isAtBottom, setIsAtBottom] = React.useState(true);
  const [unseenMessageCount, setUnseenMessageCount] = React.useState(0);
  const isAtBottomRef = React.useRef(true);
  const unseenMessageCountRef = React.useRef(0);
  const setRootElement = React.useCallback(node => {
    rootRef.current = node;
  }, []);
  const messageById = React.useMemo(() => {
    const nextMap = new Map();
    messages.forEach(message => {
      nextMap.set(message.id, message);
    });
    return nextMap;
  }, [messages]);

  // Latest-ref so `updateIsAtBottom` (which sits in many dependency arrays)
  // stays stable across inline-callback renders.
  const onReachBottomRef = React.useRef(onReachBottom);
  onReachBottomRef.current = onReachBottom;
  const updateIsAtBottom = React.useCallback(options => {
    const nextIsAtBottom = isScrollableToBottom(rootRef.current, autoScrollBuffer);
    const previousIsAtBottom = isAtBottomRef.current;
    isAtBottomRef.current = nextIsAtBottom;
    setIsAtBottom(previous => previous === nextIsAtBottom ? previous : nextIsAtBottom);

    // `onReachBottom` fires only on a `false → true` transition (strict
    // "reach" semantics): once per entry into the bottom zone, never while
    // pinned (streaming growth and appends-while-pinned are `true → true`).
    // The `silent` mode re-seeds the latch without firing — used for
    // item-set replacement (conversation switch) so switch-induced layout
    // can never fire the callback.
    if (!options?.silent && !previousIsAtBottom && nextIsAtBottom) {
      onReachBottomRef.current?.();
    }
    return nextIsAtBottom;
  }, [autoScrollBuffer]);
  const updateUnseenMessageCount = React.useCallback(nextCount => {
    unseenMessageCountRef.current = nextCount;
    setUnseenMessageCount(previous => previous === nextCount ? previous : nextCount);
  }, []);
  const captureAnchor = React.useCallback(ids => {
    const root = rootRef.current;
    if (!root) {
      return null;
    }
    const containerRect = root.getBoundingClientRect();
    for (const id of ids) {
      const element = rowElementsRef.current.get(id);
      if (!element) {
        continue;
      }
      const rect = element.getBoundingClientRect();
      if (rect.bottom <= containerRect.top || rect.top >= containerRect.bottom) {
        continue;
      }
      return {
        id,
        offsetFromBottom: containerRect.bottom - rect.bottom
      };
    }
    return null;
  }, []);
  const restoreAnchor = React.useCallback(anchor => {
    const root = rootRef.current;
    if (!root || !anchor) {
      return false;
    }
    const element = rowElementsRef.current.get(anchor.id);
    if (!element) {
      return false;
    }
    const containerBottom = root.getBoundingClientRect().bottom;
    const nextOffsetFromBottom = containerBottom - element.getBoundingClientRect().bottom;
    root.scrollTop += anchor.offsetFromBottom - nextOffsetFromBottom;
    return true;
  }, []);
  const scrollToBottom = React.useCallback(options => {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    if (typeof root.scrollTo === 'function') {
      root.scrollTo({
        top: root.scrollHeight,
        behavior: options?.behavior ?? 'auto'
      });
    } else {
      root.scrollTop = root.scrollHeight;
    }
    updateIsAtBottom();
    updateUnseenMessageCount(0);
    anchorRef.current = captureAnchor(itemIdsRef.current);
  }, [captureAnchor, updateIsAtBottom, updateUnseenMessageCount]);
  const registerRowElement = React.useCallback((id, element) => {
    if (element == null) {
      rowElementsRef.current.delete(id);
      return;
    }
    rowElementsRef.current.set(id, element);
  }, []);
  const scheduleResizeRestore = React.useCallback(() => {
    if (typeof requestAnimationFrame !== 'function') {
      // SSR / non-browser environments don't have rAF — bail rather than throw.
      return;
    }
    cancelAnimationFrame(resizeFrameRef.current);
    resizeFrameRef.current = requestAnimationFrame(() => {
      // Guard against the frame firing after unmount: once the cleanup runs
      // we shouldn't touch DOM refs or call state setters (#10).
      if (!isMountedRef.current) {
        return;
      }
      if (isAtBottomRef.current && autoScrollEnabled && isStreamingRef.current) {
        // Follow streaming content: the row grew (new tokens), scroll to stay at bottom.
        // Only auto-scroll during streaming so that user-initiated resizes (e.g.
        // expanding/collapsing tool or reasoning sections) do not cause unwanted scrolling.
        // scrollToBottom() already calls updateIsAtBottom() and captureAnchor() internally.
        scrollToBottom();
      } else {
        if (!isAtBottomRef.current) {
          restoreAnchor(anchorRef.current);
        }
        updateIsAtBottom();
        anchorRef.current = captureAnchor(itemIdsRef.current);
      }
    });
  }, [autoScrollEnabled, captureAnchor, restoreAnchor, scrollToBottom, updateIsAtBottom]);
  const maybeLoadMoreHistory = React.useCallback(async () => {
    const root = rootRef.current;
    if (!root || !hasMoreHistory || topLoadInFlightRef.current) {
      return;
    }
    if (root.scrollTop > estimatedItemSize) {
      topReachedRef.current = false;
      return;
    }
    if (topReachedRef.current) {
      return;
    }
    topReachedRef.current = true;
    topLoadInFlightRef.current = true;
    onReachTop?.();
    try {
      await loadMoreHistory();
    } finally {
      topLoadInFlightRef.current = false;
    }
  }, [estimatedItemSize, hasMoreHistory, loadMoreHistory, onReachTop]);
  const handleScroll = React.useCallback(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    anchorRef.current = captureAnchor(itemIdsRef.current);
    const nextIsAtBottom = updateIsAtBottom();
    if (nextIsAtBottom) {
      updateUnseenMessageCount(0);
    }
    if (root.scrollTop > estimatedItemSize) {
      topReachedRef.current = false;
      return;
    }
    void maybeLoadMoreHistory();
  }, [captureAnchor, estimatedItemSize, maybeLoadMoreHistory, updateIsAtBottom, updateUnseenMessageCount]);
  React.useLayoutEffect(() => {
    updateIsAtBottom();
    anchorRef.current = captureAnchor(itemIdsRef.current);
  }, [captureAnchor, updateIsAtBottom]);
  React.useLayoutEffect(() => {
    const previousItemIds = previousItemIdsRef.current;
    const changeKind = classifyItemChange(previousItemIds, itemIds);
    if (changeKind === 'prepend' || changeKind === 'other') {
      restoreAnchor(anchorRef.current);
    } else if (changeKind === 'append') {
      const appendedIds = itemIds.slice(previousItemIds.length);
      const lastAppendedMessage = appendedIds.length ? messageById.get(appendedIds[appendedIds.length - 1]) : null;
      if (autoScrollEnabled && isAtBottomRef.current || lastAppendedMessage?.role === 'user') {
        scrollToBottom();
      } else if (appendedIds.length > 0) {
        updateUnseenMessageCount(unseenMessageCountRef.current + appendedIds.length);
      }
    }
    const nextIsAtBottom = updateIsAtBottom({
      silent: changeKind === 'other'
    });
    if (nextIsAtBottom) {
      updateUnseenMessageCount(0);
    }
    anchorRef.current = captureAnchor(itemIds);
    topReachedRef.current = false;
    previousItemIdsRef.current = itemIds;
  }, [autoScrollEnabled, captureAnchor, itemIds, messageById, restoreAnchor, scrollToBottom, updateIsAtBottom, updateUnseenMessageCount]);
  React.useEffect(() => {
    // Restore on (re)mount so the resize-driven auto-scroll keeps working after
    // a StrictMode mount → cleanup → mount cycle, which would otherwise leave
    // `isMountedRef.current` stuck at `false` from the dev-only first cleanup.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(resizeFrameRef.current);
      }
    };
  }, []);
  const contextValue = React.useMemo(() => ({
    isAtBottom,
    unseenMessageCount,
    scrollToBottom
  }), [isAtBottom, scrollToBottom, unseenMessageCount]);
  return {
    contextValue,
    handleScroll,
    ownerState: {
      messageCount: itemIds.length,
      isAtBottom
    },
    registerRowElement,
    scheduleResizeRestore,
    scrollToBottom,
    setRootElement
  };
}